import {
  API_ENDPOINT_LABEL,
  API_ENDPOINTS,
  type ApiRequestRow,
  type HaciendaStatus,
} from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, Navigate, useNavigate } from '@tanstack/react-router'
import { PlugZapIcon, SettingsIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import { ClearFilters, FilterBar, FilterSearch, FilterSelect, FilterTabs } from '@/components/ui/filter-bar'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import {
  apiActivityListQuery,
  apiActivitySummaryQuery,
  type Period,
  resolveRange,
} from '@/features/api-activity/api'
import { PeriodPicker } from '@/features/api-activity/period-picker'
import { RequestSheet, shortConsecutive } from '@/features/api-activity/request-sheet'
import { UsageSummaryView } from '@/features/api-activity/usage-summary'
import { HaciendaBadge } from '@/features/documents/status'
import { formatDate, formatNumber } from '@/lib/format'
import { useActiveMembership } from '@/lib/session'

const searchSchema = z.object({
  period: z.enum(['today', '7d', '30d', 'month', 'prev', 'custom']).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  result: z.enum(['all', 'ok', 'error']).optional(),
  endpoint: z.enum(API_ENDPOINTS).optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).optional(),
  /** Llamada abierta en el panel lateral. */
  id: z.string().optional(),
})
type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/api-activity')({
  staticData: { crumb: 'Actividad API' },
  validateSearch: searchSchema,
  component: ApiActivityPage,
})

const col = columnHelper<ApiRequestRow>()

/** Actividad API es propia del plan API: con otro plan no existe (el superadmin ve el uso en Administración). */
function ApiActivityPage() {
  const membership = useActiveMembership()
  if (membership && membership.plan !== 'api') return <Navigate to="/" replace />
  return <ApiActivityContent />
}

function ApiActivityContent() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const period: Period = search.period ?? '30d'
  const range = useMemo(() => resolveRange(period, search.from, search.to), [period, search.from, search.to])

  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text.trim())
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])
  // Un filtro puesto desde fuera (p. ej. un error frecuente) se refleja en el buscador.
  useEffect(() => setText(search.q ?? ''), [search.q])

  const summary = useQuery(apiActivitySummaryQuery(range))
  const list = useQuery(
    apiActivityListQuery({
      ...range,
      result: search.result ?? 'all',
      endpoint: search.endpoint,
      search: search.q,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )

  const filtered = !!(search.q || search.endpoint || (search.result && search.result !== 'all'))
  const neverUsed = summary.data?.requests === 0 && !filtered && !summary.isFetching

  const columns = useMemo(
    () => [
      col.accessor('at', {
        header: 'Fecha y hora',
        cell: ({ getValue }) => (
          <div className="whitespace-nowrap">
            <div className="text-sm">{formatDate(getValue(), 'd MMM yyyy')}</div>
            <div className="text-muted-foreground text-xs tabular">{formatDate(getValue(), 'HH:mm:ss')}</div>
          </div>
        ),
      }),
      col.accessor('endpoint', {
        header: 'Endpoint',
        cell: ({ row }) => (
          <div className="whitespace-nowrap">
            <Badge variant="outline">{API_ENDPOINT_LABEL[row.original.endpoint]}</Badge>
            <div className="mt-1 font-mono text-[11px] text-muted-foreground">
              HTTP {row.original.httpStatus}
            </div>
          </div>
        ),
      }),
      col.accessor('ok', {
        header: 'Resultado',
        cell: ({ row }) =>
          row.original.ok ? (
            <Badge variant="success" dot>
              Correcta
            </Badge>
          ) : (
            <div className="min-w-0">
              <Badge variant="destructive" dot>
                Error
              </Badge>
              {row.original.errorMessage && (
                <div
                  className="mt-1 max-w-[15rem] truncate text-muted-foreground text-xs"
                  title={row.original.errorMessage}
                >
                  {row.original.errorMessage}
                </div>
              )}
            </div>
          ),
      }),
      col.display({
        id: 'document',
        header: 'Comprobante',
        cell: ({ row }) => {
          const r = row.original
          if (!r.documentId) return <span className="text-muted-foreground">—</span>
          return (
            <Link
              to="/documents/$id"
              params={{ id: r.documentId }}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 font-mono text-[13px] hover:text-primary hover:underline"
            >
              <Badge variant="neutral">{r.documentKind}</Badge>
              {shortConsecutive(r.consecutive)}
            </Link>
          )
        },
      }),
      col.accessor('haciendaStatus', {
        header: 'Hacienda',
        cell: ({ getValue }) =>
          getValue() ? (
            <HaciendaBadge status={getValue() as HaciendaStatus} />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      }),
      col.accessor('controlNumber', {
        header: 'Núm. control',
        meta: { hideOnMobile: true },
        cell: ({ getValue }) => (
          <span
            className="block max-w-32 truncate font-mono text-[12px] text-muted-foreground"
            title={getValue() ?? ''}
          >
            {getValue() ?? '—'}
          </span>
        ),
      }),
      col.accessor('durationMs', {
        header: 'Duración',
        meta: { align: 'right' },
        cell: ({ getValue }) => <span className="whitespace-nowrap">{formatNumber(getValue())} ms</span>,
      }),
      col.accessor('ip', {
        header: 'IP',
        meta: { className: 'max-xl:hidden' },
        cell: ({ getValue }) => (
          <span className="font-mono text-muted-foreground text-xs">{getValue() ?? '—'}</span>
        ),
      }),
    ],
    [],
  )

  return (
    <Page>
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-1.5">
            <PlugZapIcon className="size-3.5" /> Integración
          </span>
        }
        title="Actividad de la API"
        description="Lo que tus sistemas hacen con Facture: llamadas, errores y comprobantes emitidos."
        actions={
          <>
            <PeriodPicker
              value={{ period, from: search.from, to: search.to }}
              onChange={(v) => setSearch({ period: v.period, from: v.from, to: v.to, page: undefined })}
            />
            <Button asChild variant="outline">
              <Link to="/settings/api">
                <SettingsIcon /> Credenciales
              </Link>
            </Button>
          </>
        }
      />

      <UsageSummaryView
        summary={summary.data}
        onPickError={(message) => setSearch({ q: message.slice(0, 100), result: 'error', page: undefined })}
      />

      <div className="mt-10 mb-4">
        <h2 className="font-semibold text-lg tracking-tight">Registro de llamadas</h2>
        <p className="mt-1 text-muted-foreground text-sm">
          Cada llamada a la API con su resultado. Abrí una para ver lo que se envió y lo que respondió
          Facture.
        </p>
      </div>
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(r) => r.id}
        onRowClick={(r) => setSearch({ id: r.id })}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Resultado"
              value={search.result ?? 'all'}
              onValueChange={(v) =>
                setSearch({ result: v === 'all' ? undefined : (v as 'ok' | 'error'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'ok', label: 'Correctas' },
                { value: 'error', label: 'Con error' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por consecutivo o error…" />
            <FilterSelect
              label="Endpoint"
              width="xl"
              value={search.endpoint ?? 'all'}
              onValueChange={(v) =>
                setSearch({
                  endpoint: v === 'all' ? undefined : (v as (typeof API_ENDPOINTS)[number]),
                  page: undefined,
                })
              }
              options={[
                { value: 'all', label: 'Todos los endpoints' },
                ...API_ENDPOINTS.map((e) => ({ value: e, label: API_ENDPOINT_LABEL[e] })),
              ]}
            />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setSearch({ q: undefined, endpoint: undefined, result: undefined, page: undefined })
              }}
            />
          </FilterBar>
        }
        empty={
          neverUsed ? (
            <EmptyState
              icon={PlugZapIcon}
              title="Aún no hay llamadas"
              description="Cuando tus sistemas usen la API, cada llamada aparecerá aquí con su resultado. Configurá las credenciales en Configuración → API."
              action={
                <Button asChild>
                  <Link to="/settings/api">
                    <SettingsIcon /> Ir a Configuración → API
                  </Link>
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={PlugZapIcon}
              title="Sin coincidencias"
              description="No hay llamadas con esos filtros en el período elegido. Probá con otro rango o limpiá los filtros."
            />
          )
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <RequestSheet id={search.id} onClose={() => setSearch({ id: undefined })} />
    </Page>
  )
}
